import React from "react";
import { Box, SxProps, Theme } from "@mui/material";
import perspectiveUrl from "./castalia-loader-perspective.svg";
import dropSplashUrl from "./castalia-loader-drop-splash.svg";

// Cas's loading indicator: the ONE loader the app uses for anything in flight
// (Cas typing/working, connecting to Cas, page and data loads, saves). It
// replaces MUI CircularProgress/LinearProgress, which .eslintrc.json now
// forbids via no-restricted-imports.
//
// Rendered as an <img>, never inlined: each SVG carries its own <style> with
// bare class names (.ring, .drop) and @keyframes. Inside an <img> that CSS is
// scoped to the image document; inlined, it would leak into the app's global
// stylesheet.

export type CasLoaderVariant = "perspective" | "drop-splash";

const SRC: Record<CasLoaderVariant, string> = {
  perspective: perspectiveUrl,
  "drop-splash": dropSplashUrl,
};

export interface CasLoaderProps {
  /** Square edge length. Numbers are px (same default as MUI CircularProgress). */
  size?: number | string;
  variant?: CasLoaderVariant;
  /** Accessible name announced for the progressbar. */
  label?: string;
  sx?: SxProps<Theme>;
  "data-testid"?: string;
}

export const CasLoader: React.FC<CasLoaderProps> = ({
  size = 40,
  variant = "perspective",
  label = "Loading",
  sx,
  "data-testid": testId = "cas-loader",
}) => (
  <Box
    component="span"
    role="progressbar"
    aria-label={label}
    aria-busy="true"
    data-testid={testId}
    data-variant={variant}
    sx={[
      {
        display: "inline-flex",
        width: size,
        height: size,
        flexShrink: 0,
        lineHeight: 0,
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <img
      src={SRC[variant]}
      alt=""
      draggable={false}
      style={{ width: "100%", height: "100%", display: "block" }}
    />
  </Box>
);
